// NeedsAttentionPage.jsx
// Same table as the Team availability overview, limited to professionals
// flagged "Needs attention" (Status === 'red'), one row per person, with the
// Monday of the first week they have free hours as the leading column.

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FreeHoursReport from '@/components/data/hours/FreeHoursReport';
import Chargeability from '@/components/data/chargeability/Chargeability';
import StaffingRequestsSummary from './requests/StaffingRequestsSummary';
import SummaryOverview from './SummaryOverview';
import PageHeading from '@/components/common/PageHeading';
import LoadingOverlay from '@/components/common/LoadingOverlay';
import MondayDate from '@/components/data/date/MondayDate';

import { getAllocationsFuturesHoursReport, getChargeabilityReport, getProfessionals, getStaffingRequests } from '@/services/schedulingService';

import { homeCss } from '@digital_monorepo/styling';

// The fiscal year ends on 31 May. Look ahead to the end of the current one:
// May 31 of this year until then, May 31 of next year from June onwards.
// This needs the backend fix that runs the future-hours query once instead of
// once per week; before it, this many weeks took longer than API Gateway's 29s
// limit and the page showed "Data could not be loaded".
const fiscalYearEnd = (today = new Date()) => {
  const year = today.getMonth() <= 4 ? today.getFullYear() : today.getFullYear() + 1;
  return `${year}-05-31`;
};
const PERIOD_END = fiscalYearEnd();

// Weeks from this week's Monday up to and including PERIOD_END.
// The API caps the look-ahead at 104 weeks.
const weeksUntil = isoDate => {
  const today = new Date();
  const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - ((today.getDay() + 6) % 7));
  const [y, m, d] = isoDate.split('-').map(Number);
  const days = Math.round((new Date(y, m - 1, d) - monday) / 86400000);
  return Math.min(104, Math.max(1, Math.floor(days / 7) + 1));
};
const WEEKS_AHEAD = weeksUntil(PERIOD_END);

const formatMonth = yearMonth => {
  const [y, m] = yearMonth.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

const NeedsAttentionPage = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [chargeabilityReport, setChargeabilityReport] = useState(null);
  const [chargeabilityLoading, setChargeabilityLoading] = useState(true);
  const [chargeabilityError, setChargeabilityError] = useState(false);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestsError, setRequestsError] = useState(false);
  const [filters, setFilters] = useState({});

  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [directoryError, setDirectoryError] = useState(false);

  const fetchDirectory = useCallback(async () => {
    setDirectoryLoading(true);
    setDirectoryError(false);
    try {
      setDirectory(await getProfessionals());
    } catch (e) {
      console.error('Error fetching professionals:', e);
      setDirectoryError(true);
    } finally {
      setDirectoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDirectory();
  }, [fetchDirectory]);
  const photos = useMemo(() => new Map(directory.map(p => [p.ProfessionalID, p.PhotoKey])), [directory]);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(false);
      setData(await getAllocationsFuturesHoursReport(false, false, WEEKS_AHEAD));
    } catch (e) {
      console.error('Error fetching needs-attention data:', e);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchChargeability = useCallback(async () => {
    setChargeabilityLoading(true);
    setChargeabilityError(false);
    try {
      setChargeabilityReport(await getChargeabilityReport());
    } catch (e) {
      console.error('Error fetching chargeability report:', e);
      setChargeabilityError(true);
    } finally {
      setChargeabilityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChargeability();
  }, [fetchChargeability]);

  const fetchRequests = useCallback(async () => {
    setRequestsLoading(true);
    setRequestsError(false);
    try {
      setRequests(await getStaffingRequests());
    } catch (e) {
      console.error('Error fetching staffing requests:', e);
      setRequestsError(true);
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Earliest week with free hours per red-flagged professional.
  const rows = useMemo(() => {
    const byProfessional = new Map();
    data
      .filter(item => item.Status === 'red' && String(item.StartDate).slice(0, 10) <= PERIOD_END)
      .forEach(item => {
        const current = byProfessional.get(item.ProfessionalID);
        if (!current || new Date(item.StartDate) < new Date(current.StartDate)) byProfessional.set(item.ProfessionalID, item);
      });
    return [...byProfessional.values()]
      .sort((a, b) => new Date(a.StartDate) - new Date(b.StartDate))
      .map(p => ({
        ...p,
        FreeMonth: String(p.StartDate).slice(0, 7),
        PhotoKey: p.PhotoKey || photos.get(p.ProfessionalID)
      }));
  }, [data, photos]);

  const leadingColumns = useMemo(
    () => [
      {
        Header: 'Free from',
        accessor: 'StartDate',
        disableFilters: true,
        sortType: (a, b) => new Date(a.original.StartDate) - new Date(b.original.StartDate),
        Cell: ({ value }) => MondayDate.formatToUIDate(value)
      }
    ],
    []
  );

  // Flag is per professional; un-flagging someone drops them from this list.
  const handleStatusChange = (professionalID, newStatus) => {
    setData(prev => prev.map(item => (item.ProfessionalID === professionalID ? { ...item, Status: newStatus } : item)));
  };

  const handleFilterChange = (columnId, value) => setFilters(prev => ({ ...prev, [columnId]: value || undefined }));

  return (
    <div className={homeCss.pageContainer}>
      {isLoading && <LoadingOverlay />}
      <PageHeading title={t('HOMENAV.PAGES.SUMMARY')}>
        <span className='workspace-count'>
          <span className='status-dot' />
          {rows.length} professionals need attention
        </span>
      </PageHeading>
      <SummaryOverview professionals={directory} loading={directoryLoading} error={directoryError} onRetry={fetchDirectory} />
      <div className={homeCss.container}>
        <div className={homeCss.mainContent}>
          <div className={homeCss.tableResponsive}>
            {error ? (
              <div className='workspace-empty' role='alert'>
                <h2>Data could not be loaded</h2>
                <p>Please try again.</p>
                <button className='workspace-button' onClick={fetchData}>
                  Retry
                </button>
              </div>
            ) : (
              <FreeHoursReport
                weekId='needs-attention'
                title='People that need attention'
                titleSize='1.5rem'
                report={rows}
                leadingColumns={leadingColumns}
                sharedFilters={filters}
                onFilterChange={handleFilterChange}
                onStatusChange={handleStatusChange}
                groupBy='FreeMonth'
                formatGroup={formatMonth}
              />
            )}
          </div>
        </div>
      </div>
      <section className={homeCss.chargeabilitySection} aria-labelledby='summary-chargeability-title'>
        <div className={homeCss.chargeabilityHeader}>
          <div>
            <h2 id='summary-chargeability-title' className={homeCss.chargeabilityTitle}>
              <span className={homeCss.sectionIndicator} aria-hidden='true' />
              {t('CHARGEABILITY.TITLE')}
            </h2>
            <p className={homeCss.chargeabilitySubtitle}>By area · through May {PERIOD_END.slice(0, 4)}</p>
          </div>
          <div className={homeCss.chargeabilityLegend} aria-label='Chargeability thresholds'>
            <span><span className={homeCss.chargeabilityLegendWarning} aria-hidden='true'>!</span> Below 79.5%</span>
            <span><span className={homeCss.chargeabilityLegendCritical} aria-hidden='true'>!</span> Below 50%</span>
          </div>
        </div>
        {chargeabilityLoading ? (
          <div className={homeCss.chargeabilityState} role='status'>Loading chargeability…</div>
        ) : chargeabilityError ? (
          <div className={homeCss.chargeabilityState} role='alert'>
            {t('CHARGEABILITY.ERROR')}
            <button className='workspace-button' onClick={fetchChargeability}>Retry</button>
          </div>
        ) : (
          <Chargeability report={chargeabilityReport} compact endMonth={PERIOD_END.slice(0, 7)} />
        )}
      </section>
      <StaffingRequestsSummary requests={requests} loading={requestsLoading} error={requestsError} onRetry={fetchRequests} />
    </div>
  );
};

export default NeedsAttentionPage;
